import './ProductsShowcase.css'
import { ProductSlide } from './ProductSlide'
import { useEffect, useRef, useState } from 'react'

export type SlideName =
  | 'Products'
  | 'SpotBlock'
  | 'ActisebMousse'
  | 'RebalanceMat'

function getSlideName(slide: Element): SlideName {
  const names: SlideName[] = [
    'Products',
    'SpotBlock',
    'ActisebMousse',
    'RebalanceMat'
  ]

  for (let name of names) {
    if (slide.classList.contains(name)) {
      return name
    }
  }

  throw new Error('Called getSlideName with random div element')
}

function getNextSlide(slideName: SlideName): SlideName {
  switch (slideName) {
    case 'Products':
      return 'ActisebMousse'
    case 'ActisebMousse':
      return 'RebalanceMat'
    case 'RebalanceMat':
      return 'SpotBlock'
    case 'SpotBlock':
      return 'SpotBlock'
  }
}

function getPreviousSlide(slideName: SlideName): SlideName {
  switch (slideName) {
    case 'Products':
      return 'Products'
    case 'ActisebMousse':
      return 'Products'
    case 'RebalanceMat':
      return 'ActisebMousse'
    case 'SpotBlock':
      return 'RebalanceMat'
  }
}

export function ProductsShowcase() {
  const productsSlideRef = useRef<HTMLDivElement>(null)
  const actisebMousseSlideRef = useRef<HTMLDivElement>(null)
  const rebalanceMatSlideRef = useRef<HTMLDivElement>(null)
  const spotBlockSlideRef = useRef<HTMLDivElement>(null)

  const [currentSlideName, setCurrentSlideName] =
    useState<SlideName>('Products')

  useEffect(() => {
    if (
      !productsSlideRef.current ||
      !actisebMousseSlideRef.current ||
      !rebalanceMatSlideRef.current ||
      !spotBlockSlideRef.current
    ) {
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.length > 1) {
          return
        }

        const entry = entries[0]
        const movingSlideName = getSlideName(entry.target)

        if (
          movingSlideName !== 'Products' &&
          movingSlideName !== 'SpotBlock' &&
          movingSlideName !== currentSlideName
        ) {
          setCurrentSlideName(movingSlideName)
          return
        }

        const targetSlideName = entry.isIntersecting
          ? getNextSlide(movingSlideName)
          : getPreviousSlide(movingSlideName)

        setCurrentSlideName(targetSlideName)
      },
      {
        root: null,
        rootMargin: '0% 0% -100% 0%',
        threshold: 0
      }
    )

    observer.observe(productsSlideRef.current)
    observer.observe(actisebMousseSlideRef.current)
    observer.observe(rebalanceMatSlideRef.current)
    observer.observe(spotBlockSlideRef.current)

    return () => {
      observer.disconnect()
    }
  }, [currentSlideName])

  return (
    <div className="ProductsShowcase">
      <ProductSlide
        name="Products"
        ref={productsSlideRef}
        currentSlide={currentSlideName}
      />
      <ProductSlide
        name="ActisebMousse"
        ref={actisebMousseSlideRef}
        currentSlide={currentSlideName}
      />
      <ProductSlide
        name="RebalanceMat"
        ref={rebalanceMatSlideRef}
        currentSlide={currentSlideName}
      />
      <ProductSlide
        name="SpotBlock"
        ref={spotBlockSlideRef}
        currentSlide={currentSlideName}
      />
    </div>
  )
}
